import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  Plus, Trash2, Eye, EyeOff, Shield, ShieldCheck, ShieldOff, X, Users, FileText,
  Building2, Lock, ScrollText, Pencil, Save, UserPlus, ChevronDown, Ban,
  CircleCheck,
} from "lucide-react";
import { api, fmtMoney, formatApiError, DOC_TYPE_META } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Content, PageHeader, StatusBadge } from "@/components/PageChrome";
import { TablePagination } from "@/components/TablePagination";
import { toast } from "sonner";

/* ------------------------------------------------------------------ *
 * Small shared form primitives
 * ------------------------------------------------------------------ */
function Field({ label, children, hint }) {
  return (
    <label className="block">
      <div className="text-xs text-gray-600 mb-1.5">{label}</div>
      {children}
      {hint && <div className="text-[11px] text-gray-400 mt-1">{hint}</div>}
    </label>
  );
}

const inputCls =
  "w-full h-10 px-3 border border-[var(--border)] rounded-md bg-white text-sm " +
  "focus:border-[var(--primary)] focus:ring-2 focus:ring-[var(--primary)]/20 outline-none";

const CLIENT_FIELDS = [
  ["name", "Name", ""], ["email", "Email", "email"], ["phone", "Phone", "tel"],
  ["gstin", "GSTIN", ""], ["address", "Address", ""], ["city", "City", ""],
  ["state", "State", ""], ["postal_code", "Postal Code", ""],
  ["country", "Country", "text"], ["notes", "Notes", "text"],
];

const COMPANY_FIELDS = [
  ["name", "Business name", ""], ["email", "Email", "email"], ["phone", "Phone", "tel"],
  ["website", "Website", "text"], ["gstin", "GSTIN", ""], ["pan", "PAN", ""],
  ["address", "Address", ""], ["city", "City", ""], ["state", "State", ""],
  ["postal_code", "Postal Code", ""], ["country", "Country", "text"],
  ["base_currency", "Currency (INR/USD)", "text"],
  ["bank_name", "Bank name", ""], ["bank_account", "Account no.", ""],
  ["bank_ifsc", "IFSC", ""],
];

/* ------------------------------------------------------------------ *
 * Create-user modal
 * ------------------------------------------------------------------ */
function CreateUserModal({ onClose, onCreated }) {
  const [form, setForm] = useState({
    name: "", email: "", password: "", role: "user",
  });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setErr(""); setBusy(true);
    try {
      // Data-access consent is not sent: a new account starts with both
      // permission flags false; the user or an admin may grant them later.
      const { data } = await api.post("/admin/users", {
        name: form.name.trim(),
        email: form.email.trim(),
        password: form.password,
        role: form.role,
      });
      toast.success(`Account created for ${data.email}`);
      onCreated();
      onClose();
    } catch (e) {
      setErr(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4"
         onClick={onClose}>
      <div className="bg-white rounded-lg w-full max-w-lg max-h-[90vh] overflow-y-auto"
           onClick={(e) => e.stopPropagation()} data-testid="create-user-modal">
        <div className="p-5 border-b border-[var(--border)] flex items-center justify-between">
          <div>
            <div className="overline">New Account</div>
            <div className="text-lg font-medium mt-0.5">Create a user</div>
          </div>
          <button className="p-1.5 hover:bg-gray-100 rounded" onClick={onClose} aria-label="Close">
            <X className="w-4 h-4" />
          </button>
        </div>
        <form onSubmit={submit} className="p-5 space-y-4">
          <Field label="Full name">
            <input required className={inputCls} value={form.name} onChange={set("name")} data-testid="create-user-name" />
          </Field>
          <Field label="Email">
            <input required type="email" className={inputCls} value={form.email} onChange={set("email")} data-testid="create-user-email" />
          </Field>
          <Field label="Password" hint="Minimum 6 characters. Share it with the user securely.">
            <input required minLength={6} type="text" className={inputCls} value={form.password} onChange={set("password")} data-testid="create-user-password" />
          </Field>
          <Field label="Role">
            <select className={inputCls} value={form.role} onChange={set("role")} data-testid="create-user-role">
              <option value="user">User</option>
              <option value="admin">Admin</option>
            </select>
          </Field>

          <div className="space-y-1.5 p-3 rounded-md border border-[var(--border)] bg-gray-50/60">
            <div className="overline">Data access</div>
            <p className="text-[11px] text-gray-500 leading-relaxed">
              The new account starts with no admin data access. The user can grant
              it from their settings, or an admin can grant it directly from the
              account detail panel.
            </p>
          </div>

          {err && <div className="text-sm text-red-600" data-testid="create-user-error">{err}</div>}
          <div className="flex gap-2 pt-1">
            <button type="submit" disabled={busy} className="btn-primary inline-flex items-center gap-2" data-testid="create-user-submit">
              <UserPlus className="w-4 h-4" /> {busy ? "Creating…" : "Create account"}
            </button>
            <button type="button" onClick={onClose} className="px-4 h-10 border border-[var(--border)] rounded-md text-sm hover:bg-gray-50">
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ *
 * Edit-user modal (account-level fields)
 * ------------------------------------------------------------------ */
function EditUserModal({ user, onClose, onSaved }) {
  const [form, setForm] = useState({
    name: user.name || "",
    email: user.email || "",
    role: user.role || "user",
    password: "",
  });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setErr(""); setBusy(true);
    // Consent flags are intentionally NOT sent here: use the dedicated
    // PUT /admin/users/{id}/permissions endpoint instead.
    const body = {
      name: form.name.trim(),
      email: form.email.trim(),
      role: form.role,
    };
    if (form.password) body.password = form.password;
    try {
      await api.put(`/admin/users/${user.id}`, body);
      toast.success("User updated");
      onSaved();
      onClose();
    } catch (e) {
      setErr(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-lg w-full max-w-lg max-h-[90vh] overflow-y-auto"
           onClick={(e) => e.stopPropagation()} data-testid="edit-user-modal">
        <div className="p-5 border-b border-[var(--border)] flex items-center justify-between">
          <div className="min-w-0">
            <div className="overline">Edit Account</div>
            <div className="text-lg font-medium mt-0.5 truncate font-mono text-base">{user.email}</div>
          </div>
          <button className="p-1.5 hover:bg-gray-100 rounded shrink-0" onClick={onClose} aria-label="Close">
            <X className="w-4 h-4" />
          </button>
        </div>
        <form onSubmit={submit} className="p-5 space-y-4">
          <Field label="Full name">
            <input required className={inputCls} value={form.name} onChange={set("name")} data-testid="edit-user-name" />
          </Field>
          <Field label="Email">
            <input required type="email" className={inputCls} value={form.email} onChange={set("email")} data-testid="edit-user-email" />
          </Field>
          <Field label="Role">
            <select className={inputCls} value={form.role} onChange={set("role")} data-testid="edit-user-role">
              <option value="user">User</option>
              <option value="admin">Admin</option>
            </select>
          </Field>
          <Field label="Reset password" hint="Leave blank to keep the current password.">
            <input type="text" minLength={6} placeholder="New password (optional)" className={inputCls}
                   value={form.password} onChange={set("password")} data-testid="edit-user-password" />
          </Field>

          {/* Read-only: consent belongs to the user, never to the admin. */}
          <div className="space-y-2 p-3 rounded-md border border-[var(--border)] bg-gray-50/60">
            <div className="overline">Consent granted by this user</div>
            <div className="flex items-center gap-2 text-sm text-gray-700">
              {user.allow_admin_data_view ? <Eye className="w-3.5 h-3.5" /> : <EyeOff className="w-3.5 h-3.5 text-gray-400" />}
              <span>Admin may <strong>view</strong> their data</span>
              <span className="ml-auto text-xs text-gray-500">{user.allow_admin_data_view ? "granted" : "not granted"}</span>
            </div>
            <div className="flex items-center gap-2 text-sm text-gray-700">
              {user.allow_admin_data_modify ? <Eye className="w-3.5 h-3.5" /> : <EyeOff className="w-3.5 h-3.5 text-gray-400" />}
              <span>Admin may <strong>edit</strong> their data</span>
              <span className="ml-auto text-xs text-gray-500">{user.allow_admin_data_modify ? "granted" : "not granted"}</span>
            </div>
            <p className="text-[11px] text-gray-500 leading-relaxed">
              These can only be changed by the user from their own settings. Changing
              name or email above requires the edit permission; role and password are
              always yours to manage.
            </p>
          </div>

          {err && <div className="text-sm text-red-600" data-testid="edit-user-error">{err}</div>}
          <div className="flex gap-2 pt-1">
            <button type="submit" disabled={busy} className="btn-primary inline-flex items-center gap-2" data-testid="edit-user-submit">
              <Save className="w-4 h-4" /> {busy ? "Saving…" : "Save changes"}
            </button>
            <button type="button" onClick={onClose} className="px-4 h-10 border border-[var(--border)] rounded-md text-sm hover:bg-gray-50">
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ *
 * Company editor
 * ------------------------------------------------------------------ */
function CompanyEditor({ userId, company, onSaved, canModify }) {
  const [form, setForm] = useState({});
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  useEffect(() => {
    const next = {};
    COMPANY_FIELDS.forEach(([k]) => { next[k] = company?.[k] ?? ""; });
    setForm(next);
  }, [company]);

  const dirty = useMemo(
    () => COMPANY_FIELDS.some(([k]) => (form[k] ?? "") !== (company?.[k] ?? "")),
    [form, company],
  );

  // A specific profile is saved by id; the legacy endpoint is only used when the
  // account has no profiles yet (so the first one still has to be creatable).
  const url = company?.id
    ? `/admin/users/${userId}/companies/${company.id}`
    : `/admin/users/${userId}/company`;

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.put(url, {
        ...form,
        base_currency: form.base_currency === "USD" ? "USD" : "INR",
      });
      toast.success("Company profile updated");
      onSaved();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={save}>
      <div className="flex items-center justify-between mb-3">
        <div className="overline">Company Profile</div>
        <button type="submit" disabled={busy || !dirty || !canModify}
                className="text-xs px-3 h-8 border border-[var(--border)] rounded-md inline-flex items-center gap-1.5 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
                data-testid="admin-save-company">
          <Save className="w-3.5 h-3.5" /> {busy ? "Saving…" : "Save"}
        </button>
      </div>
      <div className="grid sm:grid-cols-2 gap-3">
        {COMPANY_FIELDS.map(([k, label, type]) => (
          <Field key={k} label={label}>
            <input type={type} className={inputCls} value={form[k] ?? ""}
                   onChange={set(k)} readOnly={!canModify}
                   data-testid={`admin-company-${k}`} />
          </Field>
        ))}
      </div>
      {!canModify && (
        <p className="text-[11px] text-gray-500 mt-3">
          This company profile is read-only. Editing unlocks once the user or an admin grants modification permission.
        </p>
      )}
    </form>
  );
}

/* ------------------------------------------------------------------ *
 * Permission requests
 * ------------------------------------------------------------------ */

/**
 * Ask a user for access to their data.
 *
 * Nothing is granted here: the request waits in the user's inbox and only their
 * approval flips the consent flags. The scope is spelled out so the user is not
 * agreeing to something the admin minimised.
 */
function RequestAccessModal({ user, onClose, onSent }) {
  const [scope, setScope] = useState("view");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post(`/admin/users/${user.id}/permission-requests`, { scope, reason });
      toast.success(`Request sent to ${user.email}. They will see it in their inbox.`);
      onSent();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-[60] grid place-items-center p-4"
         onClick={onClose}>
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md" onClick={(e) => e.stopPropagation()}>
        <form onSubmit={submit}>
          <div className="p-5 border-b border-[var(--border)]">
            <div className="overline">Request access</div>
            <div className="text-base font-medium mt-0.5">{user.email}</div>
            <p className="text-xs text-gray-500 mt-1.5">
              The user approves or rejects this request. You can also grant access
              directly from their detail panel, and they can revoke it later from
              their settings at any time.
            </p>
          </div>

          <div className="p-5 space-y-3">
            <div className="text-xs text-gray-600">What do you need?</div>
            {[
              ["view", "View their data",
               "Read-only access to their clients, documents and company profile."],
              ["modify", "View and edit their data",
               "Full management: create and edit clients, documents, company profiles and their account details."],
            ].map(([value, label, hint]) => (
              <label
                key={value}
                className={`flex gap-3 p-3 border rounded-md cursor-pointer ${
                  scope === value ? "border-[var(--primary)] bg-blue-50/50" : "border-[var(--border)]"
                }`}
                data-testid={`request-scope-${value}`}
              >
                <input type="radio" name="scope" value={value} checked={scope === value}
                       onChange={() => setScope(value)} className="mt-0.5" />
                <span>
                  <span className="block text-sm font-medium">{label}</span>
                  <span className="block text-xs text-gray-500 mt-0.5">{hint}</span>
                </span>
              </label>
            ))}

            <div>
              <label className="text-xs text-gray-600 block mb-1.5">
                Reason <span className="text-gray-400">(shown to the user)</span>
              </label>
              <textarea rows={3} className={inputCls} value={reason}
                        onChange={(e) => setReason(e.target.value)}
                        placeholder="e.g. Reconciling invoices raised by your accounts team"
                        data-testid="request-reason" />
            </div>
          </div>

          <div className="p-4 border-t border-[var(--border)] flex gap-2 justify-end">
            <button type="button" onClick={onClose}
                    className="px-4 h-9 border border-[var(--border)] rounded-md text-sm hover:bg-gray-50">
              Cancel
            </button>
            <button type="submit" disabled={busy}
                    className="btn-primary text-sm h-9 px-4 inline-flex items-center gap-2 disabled:opacity-50"
                    data-testid="request-submit">
              <Lock className="w-4 h-4" /> {busy ? "Sending…" : "Send request"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

const SCOPE_LABEL = { view: "View only", modify: "View and edit" };

function RequestHistory({ userId, requests, onChanged, canRequest }) {
  const [busyId, setBusyId] = useState(null);

  const cancel = async (r) => {
    if (!window.confirm("Withdraw this request?")) return;
    setBusyId(r.id);
    try {
      await api.post(`/admin/permission-requests/${r.id}/cancel`);
      toast.success("Request withdrawn");
      onChanged();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusyId(null);
    }
  };

  if (!requests.length) return null;

  return (
    <section data-testid="admin-request-history">
      <div className="overline mb-3">Your requests</div>
      <div className="card-flat divide-y divide-[var(--border)]">
        {requests.map((r) => (
          <div key={r.id} className="px-4 py-3 flex items-start justify-between gap-3 text-xs"
               data-testid={`admin-request-${r.id}`}>
            <div className="min-w-0">
              <div>
                Asked for <strong>{SCOPE_LABEL[r.scope] || r.scope}</strong>
                {r.created_at ? ` · ${new Date(r.created_at).toLocaleString()}` : ""}
              </div>
              {r.reason && <div className="text-gray-500 mt-1 truncate">“{r.reason}”</div>}
              {r.response_note && (
                <div className="text-gray-500 mt-1">{r.response_note}</div>
              )}
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <span className={`px-2 py-0.5 rounded ${
                r.status === "approved" ? "bg-green-50 text-green-700"
                  : r.status === "rejected" ? "bg-red-50 text-red-700"
                  : r.status === "cancelled" ? "bg-gray-100 text-gray-500"
                  : "bg-amber-50 text-amber-700"
              }`}>{r.status}</span>
              {r.status === "pending" && (
                <button onClick={() => cancel(r)} disabled={busyId === r.id}
                        className="px-2 h-7 border border-[var(--border)] rounded hover:bg-gray-50 disabled:opacity-50"
                        data-testid={`admin-request-cancel-${r.id}`}>
                  Withdraw
                </button>
              )}
            </div>
          </div>
        ))}
      </div>
      {canRequest && (
        <p className="text-[11px] text-gray-500 mt-2">
          A request stays pending until the user answers it.
        </p>
      )}
    </section>
  );
}

/* ------------------------------------------------------------------ *
 * Multi-company management for one account
 * ------------------------------------------------------------------ */
function MultiCompanyManager({ userId, companies, limit, used, onChanged, canModify }) {
  const [adding, setAdding] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({});
  const [busy, setBusy] = useState(false);
  const [busyId, setBusyId] = useState(null);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const atLimit = used >= limit;

  const startAdd = () => {
    setForm({ base_currency: "INR", country: "India" });
    setEditing("new");
  };

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (editing === "new") {
        await api.post(`/admin/users/${userId}/companies`, {
          ...form,
          base_currency: form.base_currency === "USD" ? "USD" : "INR",
        });
        toast.success(`Company "${form.name}" added`);
      } else {
        await api.put(`/admin/users/${userId}/companies/${editing.id}`, {
          ...form,
          base_currency: form.base_currency === "USD" ? "USD" : "INR",
        });
        toast.success("Company profile updated");
      }
      setEditing(null);
      onChanged();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const remove = async (c) => {
    const warn = `\n\nThis permanently deletes ${c.doc_count || 0} document(s) and ${
      c.client_count || 0
    } client(s) belonging to it.`;
    if (!window.confirm(`Delete company "${c.name}"?${warn}`)) return;
    setBusyId(c.id);
    try {
      const { data } = await api.delete(
        `/admin/users/${userId}/companies/${c.id}?cascade=true`);
      toast.success(
        `Company deleted${data?.docs_deleted ? ` along with ${data.docs_deleted} document(s)` : ""}`);
      onChanged();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusyId(null);
    }
  };

  const makeActive = async (c) => {
    try {
      await api.post(`/admin/users/${userId}/companies/${c.id}/default`);
      toast.success(`${c.name} is now the active company`);
      onChanged();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <section data-testid="admin-companies">
      <div className="flex items-center justify-between mb-3">
        <div>
          <div className="overline">Companies</div>
          <div className="text-[11px] text-gray-500 mt-0.5 num">
            {used} of {limit} used
          </div>
        </div>
        <button onClick={startAdd} disabled={!canModify || atLimit}
                title={atLimit ? "Account is at its company limit" : "Add a company profile"}
                className="text-xs px-3 h-8 border border-[var(--border)] rounded-md inline-flex items-center gap-1.5 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
                data-testid="admin-add-company">
          <Plus className="w-3.5 h-3.5" /> Add company
        </button>
      </div>

      {atLimit && (
        <div className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded px-3 py-2 mb-3"
             data-testid="admin-company-limit-note">
          This account is at its limit of {limit}. Raise the limit on the account before
          adding another profile.
        </div>
      )}

      <div className="space-y-2 mb-4">
        {companies.map((c) => (
          <div key={c.id} className="border border-[var(--border)] rounded-md px-4 py-3"
               data-testid={`admin-company-${c.id}`}>
            <div className="flex items-center justify-between gap-3">
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium truncate">{c.name || "Unnamed company"}</span>
                  {c.is_default && (
                    <span className="text-[10px] px-1.5 py-0.5 rounded bg-[var(--primary)] text-white">
                      Active
                    </span>
                  )}
                </div>
                <div className="text-[11px] text-gray-500 mt-0.5 num">
                  {c.gstin || "No GSTIN"} · {c.doc_count || 0} docs · {c.client_count || 0} clients
                </div>
              </div>
              <div className="flex items-center gap-1 shrink-0">
                {!c.is_default && (
                  <button onClick={() => makeActive(c)} disabled={!canModify}
                          className="text-[11px] px-2.5 h-7 border border-[var(--border)] rounded hover:bg-gray-50 disabled:opacity-40"
                          data-testid={`admin-company-activate-${c.id}`}>
                    Set active
                  </button>
                )}
                <button onClick={() => { setForm(c); setEditing(c); }} disabled={!canModify}
                        className="p-1.5 hover:bg-gray-100 rounded disabled:opacity-40"
                        title="Edit company" data-testid={`admin-company-edit-${c.id}`}>
                  <Pencil className="w-3.5 h-3.5" />
                </button>
                <button onClick={() => remove(c)} disabled={!canModify || busyId === c.id}
                        className="p-1.5 hover:bg-gray-100 rounded text-red-600 disabled:opacity-40"
                        title="Delete company" data-testid={`admin-company-delete-${c.id}`}>
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {editing && (
        <form onSubmit={save} className="border border-[var(--border)] rounded-md p-4 mb-2"
              data-testid="admin-company-form">
          <div className="overline mb-3">
            {editing === "new" ? "New company" : "Edit company"}
          </div>
          <div className="grid sm:grid-cols-2 gap-3">
            {COMPANY_FIELDS.map(([k, label, type]) => (
              <Field key={k} label={label}>
                <input type={type} className={inputCls} value={form[k] ?? ""}
                       onChange={set(k)} data-testid={`admin-company-form-${k}`} />
              </Field>
            ))}
          </div>
          <div className="flex gap-2 mt-3">
            <button type="submit" disabled={busy}
                    className="btn-primary text-xs h-8 px-3 inline-flex items-center gap-1.5 disabled:opacity-50"
                    data-testid="admin-company-form-save">
              <Save className="w-3.5 h-3.5" /> {busy ? "Saving…" : "Save company"}
            </button>
            <button type="button" onClick={() => setEditing(null)}
                    className="text-xs px-3 h-8 border border-[var(--border)] rounded-md hover:bg-gray-50">
              Cancel
            </button>
          </div>
        </form>
      )}
    </section>
  );
}

/* ------------------------------------------------------------------ *
 * Client editor
 * ------------------------------------------------------------------ */
function ClientEditor({ userId, clients, onChanged, canModify }) {
  const [editing, setEditing] = useState(null); // client object | "new"
  const [form, setForm] = useState({});
  const [busy, setBusy] = useState(false);

  const startNew = () => {
    const blank = {};
    CLIENT_FIELDS.forEach(([k, , type]) => { blank[k] = type === "email" || type === "tel" ? "" : ""; });
    blank.country = "India";
    setForm(blank); setEditing("new");
  };
  const startEdit = (c) => {
    const next = {};
    CLIENT_FIELDS.forEach(([k]) => { next[k] = c[k] ?? ""; });
    setForm(next); setEditing(c);
  };
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (editing === "new") {
        await api.post(`/admin/users/${userId}/clients`, form);
        toast.success("Client created");
      } else {
        await api.put(`/admin/users/${userId}/clients/${editing.id}`, form);
        toast.success("Client updated");
      }
      setEditing(null); onChanged();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const remove = async (c) => {
    if (!window.confirm(`Delete client ${c.name}?`)) return;
    try {
      await api.delete(`/admin/users/${userId}/clients/${c.id}`);
      toast.success("Client deleted"); onChanged();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-3">
        <div className="overline">Clients ({clients.length})</div>
        {canModify && (
          <button type="button" onClick={startNew}
                  className="text-xs px-3 h-8 border border-[var(--border)] rounded-md inline-flex items-center gap-1.5 hover:bg-gray-50"
                  data-testid="admin-add-client">
            <Plus className="w-3.5 h-3.5" /> Add client
          </button>
        )}
      </div>

      {clients.length === 0 && !editing ? (
        <div className="text-sm text-gray-500 py-4">No clients yet.</div>
      ) : (
        <div className="card-flat overflow-hidden">
          <table className="w-full text-sm">
            <tbody>
              {clients.map((c) => (
                <tr key={c.id} className="border-b border-[var(--border)] last:border-0">
                  <td className="px-4 py-2.5">
                    <div className="font-medium">{c.name || "—"}</div>
                    {(c.email || c.gstin) && (
                      <div className="text-[11px] text-gray-500 font-mono">{[c.email, c.gstin].filter(Boolean).join(" · ")}</div>
                    )}
                  </td>
                  {canModify && (
                    <td className="px-4 py-2.5 text-right whitespace-nowrap w-24">
                      <button type="button" onClick={() => startEdit(c)} className="p-1.5 hover:bg-gray-100 rounded" title="Edit client" data-testid={`admin-edit-client-${c.id}`}>
                        <Pencil className="w-3.5 h-3.5" />
                      </button>
                      <button type="button" onClick={() => remove(c)} className="p-1.5 hover:bg-gray-100 rounded text-red-600" title="Delete client" data-testid={`admin-delete-client-${c.id}`}>
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {editing && (
        <form onSubmit={save} className="mt-4 p-4 rounded-md border border-[var(--border)] bg-gray-50/60">
          <div className="overline mb-3">{editing === "new" ? "New Client" : "Edit Client"}</div>
          <div className="grid sm:grid-cols-2 gap-3">
            {CLIENT_FIELDS.map(([k, label, type]) => (
              <Field key={k} label={label}>
                <input type={type === "text" ? "text" : type} className={inputCls}
                       value={form[k] ?? ""} onChange={set(k)}
                       data-testid={`admin-client-field-${k}`} />
              </Field>
            ))}
          </div>
          <div className="flex gap-2 mt-4">
            <button type="submit" disabled={busy} className="btn-primary inline-flex items-center gap-2" data-testid="admin-save-client">
              <Save className="w-4 h-4" /> {busy ? "Saving…" : "Save client"}
            </button>
            <button type="button" onClick={() => setEditing(null)} className="px-4 h-10 border border-[var(--border)] rounded-md text-sm bg-white hover:bg-gray-50">
              Cancel
            </button>
          </div>
        </form>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ *
 * Document list with status edit + delete
 * ------------------------------------------------------------------ */
const STATUSES = ["draft", "sent", "paid", "unpaid", "overdue", "cancelled"];

function DocumentTable({ userId, documents, onChanged, canModify }) {
  const [expanded, setExpanded] = useState(null);
  const [status, setStatus] = useState("draft");
  const [busy, setBusy] = useState(false);

  const toggle = (d) => {
    const next = expanded === d.id ? null : d.id;
    setExpanded(next);
    setStatus(next ? d.status || "draft" : "draft");
  };

  const saveStatus = async (d) => {
    setBusy(true);
    try {
      // The document endpoint takes the full DocumentIn shape, so send the
      // existing record back with only the status changed.
      const body = {
        doc_type: d.doc_type, template: d.template, number: d.number,
        issue_date: d.issue_date, due_date: d.due_date || "", currency: d.currency,
        client_id: d.client_id || null, client_snapshot: d.client_snapshot || {},
        items: d.items || [], notes: d.notes || "", terms: d.terms || "",
        status, tax_mode: d.tax_mode || "auto",
      };
      await api.put(`/admin/users/${userId}/documents/${d.id}`, body);
      toast.success("Document updated");
      onChanged();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const remove = async (d) => {
    if (!window.confirm(`Delete document ${d.number}?`)) return;
    try {
      await api.delete(`/admin/users/${userId}/documents/${d.id}`);
      toast.success("Document deleted"); onChanged();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  if (!documents.length) return <div className="text-sm text-gray-500 py-4">No documents yet.</div>;

  return (
    <div className="card-flat overflow-hidden">
      <table className="w-full text-sm">
        <tbody data-testid="detail-docs">
          {documents.map((d) => (
            <React.Fragment key={d.id}>
              <tr className="border-b border-[var(--border)]">
                <td className="px-4 py-2.5 font-mono text-xs w-32">{d.number}</td>
                <td className="px-4 py-2.5 text-xs text-gray-600 w-28">{DOC_TYPE_META[d.doc_type]?.short || d.doc_type}</td>
                <td className="px-4 py-2.5 text-xs">{d.client_snapshot?.name || "-"}</td>
                <td className="px-4 py-2.5 text-right num text-xs w-28">{fmtMoney(d.total, d.currency)}</td>
                <td className="px-4 py-2.5 w-24"><StatusBadge status={d.status} /></td>
                {canModify && (
                  <td className="px-4 py-2.5 text-right whitespace-nowrap w-28">
                    <button type="button" onClick={() => toggle(d)} className="p-1.5 hover:bg-gray-100 rounded" title="Edit status">
                      {expanded === d.id ? <ChevronDown className="w-3.5 h-3.5" /> : <Pencil className="w-3.5 h-3.5" />}
                    </button>
                    <button type="button" onClick={() => remove(d)} className="p-1.5 hover:bg-gray-100 rounded text-red-600" title="Delete document">
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </td>
                )}
              </tr>
              {expanded === d.id && (
                <tr className="border-b border-[var(--border)] bg-gray-50/70">
                  <td colSpan={6} className="px-4 py-3">
                    <div className="flex items-end gap-3 flex-wrap">
                      <div className="min-w-[160px]">
                        <div className="text-xs text-gray-600 mb-1.5">Status</div>
                        <select className={inputCls} value={status} onChange={(e) => setStatus(e.target.value)} data-testid={`admin-doc-status-${d.id}`}>
                          {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                        </select>
                      </div>
                      <button type="button" onClick={() => saveStatus(d)} disabled={busy}
                              className="btn-primary h-10 inline-flex items-center gap-2" data-testid={`admin-save-doc-${d.id}`}>
                        <Save className="w-4 h-4" /> {busy ? "Saving…" : "Save"}
                      </button>
                    </div>
                  </td>
                </tr>
              )}
            </React.Fragment>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/* ------------------------------------------------------------------ *
 * Page
 * ------------------------------------------------------------------ */
/* ------------------------------------------------------------------ *
 * Per-account company limit
 * ------------------------------------------------------------------ */
function CompanyLimitControl({ user, onSaved }) {
  const [value, setValue] = useState(String(user?.company_limit ?? 3));
  const [busy, setBusy] = useState(false);

  useEffect(() => { setValue(String(user?.company_limit ?? 3)); }, [user?.company_limit]);

  const save = async () => {
    const next = parseInt(value, 10);
    if (!next || next < 1) { toast.error("Limit must be at least 1"); return; }
    setBusy(true);
    try {
      const { data } = await api.put(`/admin/users/${user.id}/company-limit`, {
        company_limit: next,
      });
      toast.success(
        `Company limit set to ${data.company_limit} (was ${data.previous ?? "?"})`);
      onSaved();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const used = user?.company_count || 0;
  const over = used > (user?.company_limit ?? 3);

  return (
    <div className="card-flat p-4" data-testid="admin-company-limit-control">
      <div className="flex items-center justify-between gap-4">
        <div>
          <div className="text-sm font-medium">Company limit</div>
          <div className="text-[11px] text-gray-500 mt-0.5 num">
            {used} profile(s) in use
            {over && <span className="text-red-600 ml-1.5">over limit</span>}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <input type="number" min={1} max={100} value={value}
                 onChange={(e) => setValue(e.target.value)}
                 className="w-20 h-9 px-2 border border-[var(--border)] rounded-md text-sm"
                 data-testid="admin-company-limit-input" />
          <button onClick={save} disabled={busy || String(value) === String(user?.company_limit)}
                  className="text-xs px-3 h-9 border border-[var(--border)] rounded-md hover:bg-gray-50 disabled:opacity-40"
                  data-testid="admin-company-limit-save">
            {busy ? "Saving…" : "Set limit"}
          </button>
        </div>
      </div>
      <p className="text-[11px] text-gray-500 mt-2">
        Lets this account hold more company profiles. It grants no access to their data.
      </p>
    </div>
  );
}

export default function Admin() {
  const { user: me } = useAuth();
  const [users, setUsers] = useState([]);
  const [stats, setStats] = useState(null);
  const [selected, setSelected] = useState(null);
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [showRequest, setShowRequest] = useState(null); // user object
  const [requests, setRequests] = useState([]);
  // Which of the account's companies the drawer is showing.
  const [viewCompany, setViewCompany] = useState("");
  // Users-table filter + pagination state.
  const [userSearch, setUserSearch] = useState("");
  const [userRole, setUserRole] = useState("");
  const [userPage, setUserPage] = useState(0);
  const [userPageSize, setUserPageSize] = useState(25);
  const [userTotal, setUserTotal] = useState(0);
  const [permissionBusy, setPermissionBusy] = useState(false);

  const userParams = (search, role, skip, size) => {
    const params = { skip: skip || 0, limit: size };
    if (search) params.search = search;
    if (role) params.role = role;
    return params;
  };

  const load = async (search = userSearch, role = userRole, skip = userPage * userPageSize, size = userPageSize) => {
    setLoading(true);
    try {
      const [u, s, c] = await Promise.all([
        api.get("/admin/users", { params: userParams(search, role, skip, size) }),
        api.get("/admin/stats"),
        api.get("/admin/users/count", {
          params: { ...(search ? { search } : {}), ...(role ? { role } : {}) },
        }),
      ]);
      setUsers(Array.isArray(u.data) ? u.data : []);
      setStats(s.data);
      setUserTotal(c?.data?.count ?? (Array.isArray(u.data) ? u.data.length : 0));
    } catch (e) { toast.error(formatApiError(e)); }
    setLoading(false);
  };
  useEffect(() => { load("", "", 0, userPageSize); }, []); // eslint-disable-line

  // Debounced so typing doesn't fire a request per keystroke.
  useEffect(() => {
    const handle = setTimeout(() => { load(userSearch, userRole, 0, userPageSize); }, 250);
    return () => clearTimeout(handle);
  }, [userSearch, userRole, userPageSize]); // eslint-disable-line

  const changeUserPage = (p) => { setUserPage(p); load(userSearch, userRole, p * userPageSize, userPageSize); };
  const changeUserPageSize = (n) => { setUserPageSize(n); setUserPage(0); load(userSearch, userRole, 0, n); };

  const openDetail = async (user, companyId = "") => {
    setSelected(user); setDetail(null); setViewCompany(companyId);
    loadRequests(user.id);
    try {
      const params = companyId ? { company_id: companyId } : {};
      const { data } = await api.get(`/admin/users/${user.id}`, { params });
      setDetail(data);
      setViewCompany(data.company_id || "");
    } catch (e) {
      toast.error(formatApiError(e));
      setSelected(null);
    }
  };

  // Re-fetch the drawer for a different company on the same account.
  const switchViewedCompany = async (companyId) => {
    if (!selected) return;
    setViewCompany(companyId);
    try {
      const { data } = await api.get(`/admin/users/${selected.id}`, {
        params: companyId ? { company_id: companyId } : {},
      });
      setDetail(data);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const loadRequests = async (userId) => {
    try {
      const { data } = await api.get(`/admin/users/${userId}/permission-requests`);
      setRequests(Array.isArray(data) ? data : []);
    } catch {
      setRequests([]);
    }
  };

  const refreshAll = async () => {
    await refreshDetail();
    if (selected) await loadRequests(selected.id);
  };

  // Refreshes the open drawer when there is one, and always the users table
  // so edits made straight from a table row still land in the list.
  const refreshDetail = async () => {
    if (selected) {
      try {
        const { data } = await api.get(`/admin/users/${selected.id}`, {
          params: viewCompany ? { company_id: viewCompany } : {},
        });
        setDetail(data);
      } catch { /* keep the previous view */ }
    }
    await load();
  };

  /**
   * Role is platform administration, entirely separate from data consent: being
   * an admin never grants access to another account's business data. Promote and
   * demote are deliberately explicit rather than a blind toggle, because a
   * demotion can lock an account out of the admin area.
   */
  const toggleRole = async (user) => {
    const newRole = user.role === "admin" ? "user" : "admin";
    if (newRole === "admin" && user.id === me?.id) {
      toast.error("You are already an admin");
      return;
    }
    if (newRole === "user") {
      if (!window.confirm(
        `Demote ${user.email} to a regular user?\n\n` +
        `They will lose access to the Admin area. Access to their own business data ` +
        `is unaffected, and any permission they granted you stays in place.`,
      )) return;
      if (user.id === me?.id && Number(stats?.total_admins) <= 1) {
        toast.error("Cannot demote the only admin");
        return;
      }
    }
    try {
      await api.put(`/admin/users/${user.id}`, { role: newRole });
      toast.success(`Role updated to ${newRole}`);
      await load();
      if (selected?.id === user.id) await refreshDetail();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const remove = async (user) => {
    if (!window.confirm(`Delete ${user.email}? This removes all their data and cannot be undone.`)) return;
    try {
      await api.delete(`/admin/users/${user.id}`);
      toast.success("User deleted"); setSelected(null); await load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const applyPermissions = async (user, patch, message) => {
    setPermissionBusy(true);
    try {
      await api.put(`/admin/users/${user.id}/permissions`, patch);
      toast.success(message);
      await load();
      if (selected?.id === user.id) await refreshDetail();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setPermissionBusy(false);
    }
  };

  const toggleBlock = async (user) => {
    if (user.id === me?.id) {
      toast.error("You cannot block your own account");
      return;
    }
    if (!user.is_blocked) {
      const reason = window.prompt(
        `Block ${user.email}?\n\nThey will be signed out, cannot sign in, and will receive a block email with the support contact.\n\nReason (optional):`,
        "",
      );
      if (reason === null) return;
      try {
        await api.post(`/admin/users/${user.id}/block`, {
          reason: reason.trim().slice(0, 500),
        });
        toast.success(`${user.email} has been blocked and notified`);
        await load();
        if (selected?.id === user.id) await refreshDetail();
      } catch (e) { toast.error(formatApiError(e)); }
      return;
    }
    if (!window.confirm(`Unblock ${user.email}? They will be able to sign in again and will receive an email.`)) return;
    try {
      await api.post(`/admin/users/${user.id}/unblock`);
      toast.success(`${user.email} has been unblocked`);
      await load();
      if (selected?.id === user.id) await refreshDetail();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const canModify = Boolean(detail?.can_modify_data);
  const canView = Boolean(detail?.can_view_data);

  return (
    <>
      <PageHeader
        overline="Admin"
        title="Platform Administration"
        description="Create accounts, manage users, and grant or withdraw data access. Changes are recorded in the audit log."
        testid="page-admin"
        actions={
          <>
            <button onClick={() => setShowCreate(true)} className="btn-primary text-sm h-10 inline-flex items-center gap-2" data-testid="btn-create-user">
              <UserPlus className="w-4 h-4" /> New user
            </button>
            <Link to="/app/admin/settings" className="text-sm px-4 h-10 border border-[var(--border)] rounded-md hover:bg-white inline-flex items-center gap-2" data-testid="btn-open-admin-settings">
              <ShieldCheck className="w-4 h-4" /> Settings
            </Link>
            <Link to="/app/admin/activity" className="text-sm px-4 h-10 border border-[var(--border)] rounded-md hover:bg-white inline-flex items-center gap-2" data-testid="btn-open-activity">
              <ScrollText className="w-4 h-4" /> Activity Log
            </Link>
            <Link to="/app/admin/audit" className="text-sm px-4 h-10 border border-[var(--border)] rounded-md hover:bg-white inline-flex items-center gap-2" data-testid="btn-open-audit">
              <ShieldCheck className="w-4 h-4" /> Audit Log
            </Link>
          </>
        }
      />
      <Content>
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            <div className="card-flat p-5" data-testid="admin-stat-users">
              <div className="overline flex items-center gap-1.5"><Users className="w-3 h-3" /> Users</div>
              <div className="text-3xl font-medium mt-3 num">{stats.total_users}</div>
              <div className="text-[11px] text-gray-500 mt-1">{stats.total_admins} admin(s)</div>
            </div>
            <div className="card-flat p-5" data-testid="admin-stat-docs">
              <div className="overline flex items-center gap-1.5"><FileText className="w-3 h-3" /> Documents</div>
              <div className="text-3xl font-medium mt-3 num">{stats.total_documents}</div>
            </div>
            <div className="card-flat p-5" data-testid="admin-stat-companies">
              <div className="overline flex items-center gap-1.5"><Building2 className="w-3 h-3" /> Companies</div>
              <div className="text-3xl font-medium mt-3 num">{stats.total_companies}</div>
            </div>
            <div className="card-flat p-5" data-testid="admin-stat-billed">
              <div className="overline">Platform Billed</div>
              <div className="text-2xl font-medium mt-3 num">{fmtMoney(stats.platform_billed)}</div>
              <div className="text-[11px] text-gray-500 mt-1">GST {fmtMoney(stats.platform_gst)}</div>
            </div>
          </div>
        )}

        {stats && (
          <div className="card-flat p-4 mb-6 flex items-center gap-4 text-sm">
            <ShieldCheck className="w-4 h-4 text-[var(--primary)]" />
            <div className="flex-1">
              <span className="font-medium">{stats.granted_view}</span> users allow <strong>viewing</strong> ·
              <span className="font-medium ml-2">{stats.granted_modify}</span> allow <strong>editing</strong>
            </div>
          </div>
        )}

        {/* Users table */}
        <div className="card-flat overflow-hidden">
          <div className="px-5 py-4 border-b border-[var(--border)] flex items-center justify-between gap-3 flex-wrap">
            <div>
              <div className="overline">All Users</div>
              <div className="text-lg font-medium mt-0.5" data-testid="admin-users-total">
                {userTotal} registered account(s)
              </div>
            </div>
            <div className="flex items-center gap-2">
              <input
                value={userSearch}
                onChange={(e) => setUserSearch(e.target.value)}
                placeholder="Search name or email…"
                className="h-9 w-56 px-3 border border-[var(--border)] rounded-md bg-white text-sm"
                data-testid="admin-users-search"
              />
              <select
                value={userRole}
                onChange={(e) => { setUserRole(e.target.value); setUserPage(0); }}
                className="h-9 px-3 border border-[var(--border)] rounded-md bg-white text-sm"
                data-testid="admin-users-role"
              >
                <option value="">All roles</option>
                <option value="admin">Admins</option>
                <option value="user">Users</option>
              </select>
              <button onClick={() => setShowCreate(true)} className="text-sm px-3 h-9 border border-[var(--border)] rounded-md inline-flex items-center gap-1.5 hover:bg-gray-50 whitespace-nowrap" data-testid="btn-create-user-2">
                <Plus className="w-4 h-4" /> Add
              </button>
            </div>
          </div>
          {loading && users.length === 0 ? (
            <div className="p-8 text-sm text-gray-500">Loading…</div>
          ) : users.length === 0 ? (
            <div className="p-10 text-center text-sm text-gray-500" data-testid="admin-users-empty">
              No account matches this filter.
            </div>
          ) : (
            // Fragment: the scroll container and the pager are siblings, so
            // they need one enclosing element or JSX rejects the pair.
            <>
              <div className="overflow-x-auto">
              <table className="w-full text-sm min-w-[760px]">
                <thead className="text-left text-[11px] text-gray-500 uppercase tracking-wider">
                  <tr className="border-b border-[var(--border)]">
                    <th className="px-5 py-3 font-medium">User</th>
                    <th className="px-5 py-3 font-medium">Role</th>
                    <th className="px-5 py-3 font-medium">Data Access</th>
                    <th className="px-5 py-3 font-medium text-right">Companies</th>
                    <th className="px-5 py-3 font-medium text-right">Docs</th>
                    <th className="px-5 py-3 font-medium text-right">Clients</th>
                    <th className="px-5 py-3 font-medium">Joined</th>
                    <th className="px-5 py-3"></th>
                  </tr>
                </thead>
                <tbody data-testid="admin-users-table">
                  {users.map((u) => (
                    <tr key={u.id} className="border-b border-[var(--border)] hover:bg-gray-50 cursor-pointer" onClick={() => openDetail(u)}>
                      <td className="px-5 py-3">
                        <div className="font-medium flex items-center gap-2">
                          {u.name}
                          {u.is_blocked && (
                            <span className="text-[10px] px-1.5 py-0.5 rounded bg-red-50 text-red-700 border border-red-200"
                                  data-testid={`blocked-badge-${u.id}`}>
                              blocked
                            </span>
                          )}
                        </div>
                        <div className="text-xs text-gray-500 font-mono">{u.email}</div>
                      </td>
                      <td className="px-5 py-3">
                        <span
                          title="Role controls admin access only — it does not grant access to this user's data"
                          className={`text-[11px] px-2 py-1 rounded-full font-mono uppercase ${u.role === "admin" ? "bg-blue-100 text-blue-800" : "bg-gray-100 text-gray-700"}`}>
                          {u.role}
                        </span>
                      </td>
                      <td className="px-5 py-3">
                        <div className="flex items-center gap-1.5" title="View / Modify permissions">
                          {u.allow_admin_data_view ? <Eye className="w-3.5 h-3.5 text-green-600" /> : <EyeOff className="w-3.5 h-3.5 text-gray-300" />}
                          <span className="text-[10px] font-mono">V</span>
                          {u.allow_admin_data_modify ? <ShieldCheck className="w-3.5 h-3.5 text-green-600 ml-2" /> : <ShieldOff className="w-3.5 h-3.5 text-gray-300 ml-2" />}
                          <span className="text-[10px] font-mono">M</span>
                        </div>
                      </td>
                      <td className="px-5 py-3 text-right num">
                        {u.company_count || 0}
                        <span className="text-gray-400"> / {u.company_limit ?? 3}</span>
                      </td>
                      <td className="px-5 py-3 text-right num">{u.doc_count}</td>
                      <td className="px-5 py-3 text-right num">{u.client_count}</td>
                      <td className="px-5 py-3 text-xs text-gray-500 font-mono">{(u.created_at || "").slice(0, 10)}</td>
                      <td className="px-5 py-3 text-right whitespace-nowrap">
                        <button onClick={(e) => { e.stopPropagation(); setEditingUser(u); }} className="p-2 hover:bg-gray-100 rounded" title="Edit user" data-testid={`edit-user-${u.id}`}>
                          <Pencil className="w-4 h-4" />
                        </button>
                        <button onClick={(e) => { e.stopPropagation(); setShowRequest(u); }}
                                className="p-2 hover:bg-gray-100 rounded"
                                title="Request access to this user's data"
                                data-testid={`request-access-${u.id}`}>
                          <Lock className="w-4 h-4" />
                        </button>
                        <button onClick={(e) => { e.stopPropagation(); toggleBlock(u); }}
                                className={`p-2 hover:bg-gray-100 rounded ${u.is_blocked ? "text-green-700" : "text-amber-700"}`}
                                title={u.is_blocked ? "Unblock user" : "Block user"}
                                data-testid={`toggle-block-${u.id}`}>
                          {u.is_blocked ? <CircleCheck className="w-4 h-4" /> : <Ban className="w-4 h-4" />}
                        </button>
                        <button onClick={(e) => { e.stopPropagation(); toggleRole(u); }} className="p-2 hover:bg-gray-100 rounded" title={u.role === "admin" ? "Demote to user" : "Promote to admin"} data-testid={`toggle-role-${u.id}`}>
                          <Shield className="w-4 h-4" />
                        </button>
                        <button onClick={(e) => { e.stopPropagation(); remove(u); }} className="p-2 hover:bg-gray-100 rounded text-red-600" title="Delete user" data-testid={`delete-user-${u.id}`}>
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="px-5 pb-4">
              <TablePagination
                page={userPage}
                pageCount={Math.ceil(userTotal / userPageSize)}
                total={userTotal}
                pageSize={userPageSize}
                onPage={changeUserPage}
                onPageSize={changeUserPageSize}
                idPrefix="admin-users"
                noun="account"
              />
            </div>
            </>
          )}
        </div>
      </Content>

      {showCreate && (
        <CreateUserModal onClose={() => setShowCreate(false)} onCreated={load} />
      )}
      {editingUser && (
        <EditUserModal user={editingUser} onClose={() => setEditingUser(null)} onSaved={refreshDetail} />
      )}
      {showRequest && (
        <RequestAccessModal user={showRequest} onClose={() => setShowRequest(null)} onSent={refreshAll} />
      )}

      {/* Detail drawer */}
      {selected && (
        <div className="fixed inset-0 bg-black/40 z-50 flex justify-end" onClick={() => setSelected(null)}>
          <div className="bg-white w-full max-w-3xl h-full overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="p-5 border-b border-[var(--border)] flex items-center justify-between sticky top-0 bg-white z-10">
              <div className="min-w-0">
                <div className="overline">User Detail</div>
                <div className="text-lg font-medium mt-0.5 truncate">
                  {selected.name} · <span className="font-mono text-sm text-gray-600">{selected.email}</span>
                </div>
              </div>
              <div className="flex items-center gap-1 shrink-0">
                <button onClick={() => setShowRequest(detail || selected)}
                        className="p-2 hover:bg-gray-100 rounded"
                        title="Request access to this user's data" data-testid="drawer-request-access">
                  <Lock className="w-4 h-4" />
                </button>
                <button onClick={() => setEditingUser(detail || selected)} className="p-2 hover:bg-gray-100 rounded" title="Edit user" data-testid="drawer-edit-user">
                  <Pencil className="w-4 h-4" />
                </button>
                <button onClick={() => toggleBlock(detail || selected)}
                        className={`p-2 hover:bg-gray-100 rounded ${(detail || selected)?.is_blocked ? "text-green-700" : "text-amber-700"}`}
                        title={(detail || selected)?.is_blocked ? "Unblock user" : "Block user"}
                        data-testid="drawer-toggle-block">
                  {(detail || selected)?.is_blocked ? <CircleCheck className="w-4 h-4" /> : <Ban className="w-4 h-4" />}
                </button>
                <button onClick={() => setSelected(null)} className="p-1.5 hover:bg-gray-100 rounded" aria-label="Close">
                  <X className="w-4 h-4" />
                </button>
              </div>
            </div>

            <div className="p-5 space-y-8">
              {(detail || selected)?.is_blocked && (
                <div className="card-flat p-4 bg-red-50 border-red-200 text-sm text-red-900"
                     data-testid="detail-blocked-notice">
                  <div className="flex items-center gap-2 font-medium">
                    <Ban className="w-4 h-4" /> This account is blocked
                  </div>
                  <div className="text-xs mt-1">
                    Signed in sessions are denied immediately. The user was emailed the
                    support contact.
                    {(detail || selected)?.block_reason ? ` Reason: ${(detail || selected).block_reason}` : ""}
                  </div>
                  <div className="text-xs mt-1 font-mono">
                    Support: {(detail || selected)?.support_email || "support@prismgrim.com"}
                  </div>
                </div>
              )}

              <section data-testid="admin-permission-control">
                <div className="overline mb-3">Administrator data access</div>
                <div className="card-flat p-4 space-y-3">
                  <p className="text-xs text-gray-500">
                    Grant or withdraw consent directly. The user can still change these
                    from their own Settings; requests waiting in their inbox are withdrawn.
                  </p>
                  {[
                    ["allow_admin_data_view", "View data"],
                    ["allow_admin_data_modify", "View and edit data"],
                  ].map(([field, label]) => {
                    const on = Boolean((detail || selected)?.[field]);
                    return (
                      <div key={field} className="flex items-center justify-between gap-3">
                        <span className="text-sm">{label}</span>
                        <button type="button" disabled={permissionBusy}
                                onClick={() => applyPermissions(
                                  detail || selected,
                                  { [field]: !on },
                                  on ? "Administrator access withdrawn" : "Administrator access granted",
                                )}
                                className={`text-xs px-3 h-8 rounded-md border disabled:opacity-50 ${
                                  on
                                    ? "border-green-200 bg-green-50 text-green-700"
                                    : "border-[var(--border)] hover:bg-gray-50"
                                }`}
                                data-testid={`admin-permission-${field}`}>
                          {on ? "Granted — withdraw" : "Not granted — allow"}
                        </button>
                      </div>
                    );
                  })}
                  <div className="flex gap-2 pt-1">
                    <button type="button" disabled={permissionBusy}
                            onClick={() => applyPermissions(
                              detail || selected,
                              { allow_admin_data_view: true, allow_admin_data_modify: true },
                              "Full administrator access granted",
                            )}
                            className="text-xs px-3 h-8 rounded-md bg-[var(--fg)] text-white disabled:opacity-50"
                            data-testid="admin-permission-grant-all">
                      Grant all access
                    </button>
                    <button type="button" disabled={permissionBusy}
                            onClick={() => applyPermissions(
                              detail || selected,
                              { allow_admin_data_view: false, allow_admin_data_modify: false },
                              "All administrator access withdrawn",
                            )}
                            className="text-xs px-3 h-8 rounded-md border border-red-200 text-red-700 hover:bg-red-50 disabled:opacity-50"
                            data-testid="admin-permission-revoke-all">
                      Withdraw all access
                    </button>
                  </div>
                </div>
              </section>

              <CompanyLimitControl user={detail || selected} onSaved={refreshAll} />
              {!detail ? (
                <div className="text-sm text-gray-500">Loading…</div>
              ) : !canView ? (
                <div className="card-flat p-8 text-center bg-amber-50 border-amber-200" data-testid="detail-locked">
                  <Lock className="w-8 h-8 mx-auto text-amber-600 mb-3" />
                  <div className="font-medium">Data access not granted</div>
                  <div className="text-sm text-gray-600 mt-2 max-w-sm mx-auto">
                    {selected.name} has not granted permission for administrators to view their data. You can still
                    change their role, adjust their company limit, or delete their account — but not see their
                    invoices, receipts or clients until they approve a request.
                  </div>
                  <button onClick={() => setShowRequest(selected)}
                          className="btn-primary text-sm h-9 px-4 mt-5 inline-flex items-center gap-2"
                          data-testid="detail-request-access">
                    <Lock className="w-4 h-4" /> Request access
                  </button>
                </div>
              ) : (
                <>
                  {!canModify && (
                    <div className="card-flat p-4 bg-blue-50 border-blue-200 flex items-center gap-3 text-sm" data-testid="detail-readonly-notice">
                      <Eye className="w-4 h-4 text-blue-700 shrink-0" />
                      <div className="text-blue-900">
                        Read-only — this user has granted <strong>view</strong> but not <strong>edit</strong> permission.
                        <button onClick={() => setShowRequest(detail)} className="ml-2 underline underline-offset-2 font-medium"
                                data-testid="detail-request-edit">
                          Ask for edit access
                        </button>
                      </div>
                    </div>
                  )}

                  {detail.companies?.length > 1 && (
                    <div className="flex gap-1.5 flex-wrap" data-testid="detail-company-tabs">
                      {detail.companies.map((c) => (
                        <button key={c.id} onClick={() => switchViewedCompany(c.id)}
                                className={`px-3 h-8 text-xs rounded-md border ${
                                  (viewCompany || detail.company_id) === c.id
                                    ? "border-[var(--primary)] bg-blue-50 text-[var(--primary)] font-medium"
                                    : "border-[var(--border)] hover:bg-gray-50"
                                }`}
                                data-testid={`detail-company-tab-${c.id}`}>
                          {c.name || "Unnamed company"}
                          <span className="num text-gray-400 ml-1.5">{c.doc_count || 0}</span>
                        </button>
                      ))}
                    </div>
                  )}

                  {detail.totals && (
                    <div className="grid grid-cols-3 gap-3">
                      <div className="card-flat p-3">
                        <div className="text-[10px] text-gray-500 uppercase">Invoices</div>
                        <div className="text-xl font-medium num mt-1">{detail.totals.invoices}</div>
                      </div>
                      <div className="card-flat p-3">
                        <div className="text-[10px] text-gray-500 uppercase">Billed</div>
                        <div className="text-lg font-medium num mt-1">{fmtMoney(detail.totals.billed)}</div>
                      </div>
                      <div className="card-flat p-3">
                        <div className="text-[10px] text-gray-500 uppercase">GST Collected</div>
                        <div className="text-lg font-medium num mt-1">{fmtMoney(detail.totals.gst)}</div>
                      </div>
                    </div>
                  )}

                  <RequestHistory userId={selected.id} requests={requests}
                                  onChanged={refreshAll} canRequest={!canModify} />

                  <MultiCompanyManager
                    userId={selected.id}
                    companies={detail.companies || []}
                    limit={detail.company_limit ?? 3}
                    used={detail.company_count || (detail.companies || []).length}
                    onChanged={refreshAll}
                    canModify={canModify}
                  />

                  <section>
                    <CompanyEditor userId={selected.id} company={detail.company} onSaved={refreshAll} canModify={canModify} />
                  </section>

                  <section>
                    <ClientEditor userId={selected.id} clients={detail.clients || []} onChanged={refreshAll} canModify={canModify} />
                  </section>

                  <section>
                    <div className="overline mb-3">Documents ({detail.documents?.length || 0})</div>
                    <DocumentTable userId={selected.id} documents={detail.documents || []} onChanged={refreshAll} canModify={canModify} />
                  </section>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}